import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAudioTranscription,
  postAudioTranscriptionLocal,
} from "../../../services/api/instances/audioTranscriptionApi/methods";
import {
  MeetingsValue,
  TSpeakerContent,
} from "../../../services/api/instances/meetingsApi/types";
import { RootState } from "../../../services/redux/types";
import { editStorageObject } from "../../../app/state/storageObjectSlice/actions/actions";
import { getProcessingTime } from "../../Modals/AudioTranscription/helpers";
import i18n from "../../../services/i18n";
import { getUpdatedMeetingValue } from "../helpers/getUpdatedValue";
import { TTranscriptionArray } from "../../../services/api/instances/audioTranscriptionApi/types";
import {
  clearMessages,
  setMeetingValue,
  updateMeetingValueField,
  updateRecognizeMeta,
} from "./slice";
import { generateFinalResultText } from "../helpers/generateFinalResultText";
import { TChatCompletionRequest } from "../../../services/api/instances/completionsApi/types";
import {
  deleteThread,
  getMessagesFromThread,
  postMessageToCompletion,
  postMessageToResponse,
} from "../../../services/api/instances/responseApi/api";
import {
  mergedDialogMessages,
  transformMessagesObject,
} from "../../Dialog/helpers";
import { hiddenSecurityPart } from "../../../shared/utils/hiddenSecurityPart";
import { getSpeechTimeCode } from "../helpers/getSpeechTimeCode";
import { TResponseApiRequestBody } from "../../../services/api/instances/responseApi/types/request";
import { OutputMessageType } from "../../../services/api/instances/responseApi/types/common";
import { generateId } from "../../../shared/utils/generateId";
import { MEETING_META_INSTRUCTION } from "../constants";
import { createNotification } from "../../../app/state/notificationSlice/slice";
import { getError } from "../../../services/lib/getError";

export const postAudioFileAction = createAsyncThunk(
  "meeting/postAudioFile",
  async (file: File, { dispatch, getState, rejectWithValue }) => {
    try {
      const { selectedId, data } = (getState() as RootState).storageObjectState[
        "meetings"
      ];
      const { meetingValue } = (getState() as RootState).meetingState;
      const { selectedLang } = (getState() as RootState).i18n;

      if (!meetingValue?.meetings_object || !selectedId) return;

      dispatch(
        updateRecognizeMeta(selectedId, {
          is_file_loading: true,
        }),
      );

      const response = await postAudioTranscriptionLocal(file as File);

      if (!response) {
        dispatch(
          updateRecognizeMeta(selectedId, {
            is_file_loading: false,
          }),
        );
        return;
      }

      const selectedObject = data.entities[selectedId];
      const processingTime = getProcessingTime(
        file.size,
        response.estimate_duration,
        selectedLang,
      );
      const loadingInfo = `${i18n.t("audio_transcription.file_upload_message")}`;
      const { meeting_name, meeting_date, meeting_description, _user_edited } =
        meetingValue?.meetings_object.meeting_info;
      const newValue: MeetingsValue = getUpdatedMeetingValue(meetingValue, {
        recognize_hash: response.hash,
        meta: {
          ...meetingValue.meetings_object.meta,
          estimate_duration: `${loadingInfo} ${processingTime}`,
          progress: "0%",
        },
        meeting_info: {
          meeting_name: _user_edited ? (meeting_name ?? file.name) : file.name,
          meeting_date: _user_edited
            ? (meeting_date ?? new Date(file.lastModified).toISOString())
            : new Date(file.lastModified).toISOString(),
          meeting_description: _user_edited ? (meeting_description ?? "") : "",
        },
      });
      dispatch(
        updateRecognizeMeta(selectedId, {
          is_file_loading: false,
          recognize_mode: "info",
        }),
      );
      dispatch(
        editStorageObject({
          id: selectedId,
          name: selectedObject.friendly_name,
          value: newValue,
          service: "meetings",
        }),
      );

      return newValue;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getAudioTranscriptionAction = createAsyncThunk(
  "meeting/getAudioTranscription",
  async (hash: string, { getState, dispatch, signal, rejectWithValue }) => {
    try {
      const { selectedId, data } = (getState() as RootState).storageObjectState[
        "meetings"
      ];
      if (!selectedId) return;

      const selectedObject = data.entities[selectedId];

      if (
        selectedObject.value == null ||
        !(selectedObject.value as MeetingsValue)?.meetings_object
      )
        return;

      const response = await getAudioTranscription(hash, signal);
      const value = selectedObject.value as MeetingsValue;
      const { meta } = value.meetings_object;
      const result: TSpeakerContent[] = [
        {
          id: `speaker_${generateId()}`,
          speaker_group_key: "",
          speaker_name: "Новый спикер",
          speech_content: response.text,
          isActive: false,
          time_code: getSpeechTimeCode(response.started_time),
        },
      ];

      const newValue: MeetingsValue = getUpdatedMeetingValue(value, {
        recognize_hash:
          response.status === "completed" || response.status === "failed"
            ? null
            : value.meetings_object.recognize_hash,
        meta: {
          progress: response.progress,
          status: response.status,
          estimate_duration:
            response.status === "completed" || response.status === "failed"
              ? null
              : value.meetings_object?.meta?.estimate_duration,
        },
        transcription: result,
        final_result: result.length
          ? generateFinalResultText(value.meetings_object.meeting_info, result)
          : null,
      });

      if (response.status === "completed") {
        dispatch(
          updateRecognizeMeta(selectedId, {
            recognize_mode: "upload",
            is_recognize: false,
          }),
        );
      } else if (response.status === "failed") {
        dispatch(
          updateRecognizeMeta(selectedId, {
            recognize_mode: "upload",
            is_recognize: false,
          }),
        );
        dispatch(
          createNotification(
            "warning",
            "В процессе распознавания файла произошла ошибка",
          ),
        );
      }

      if (response.progress === meta.progress) {
        return newValue;
      }

      dispatch(
        editStorageObject({
          id: selectedId,
          name: selectedObject.friendly_name,
          value: newValue,
          service: "meetings",
        }),
      );

      return newValue;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const meetingChatWithResponsesAction = createAsyncThunk(
  "meeting/meetingChatWithResponses",
  async (message: string, { getState, dispatch, rejectWithValue }) => {
    try {
      const { meetingValue } = (getState() as RootState).meetingState;
      const { selectedId, data } = (getState() as RootState).storageObjectState
        .meetings;
      if (!selectedId) return;
      if (!meetingValue || !meetingValue.meetings_object) return;
      const selectedObject = data.entities[selectedId];
      const { llm_selection, thread_ref, final_result } =
        meetingValue.meetings_object;
      const thread =
        thread_ref && thread_ref !== "" ? thread_ref : `thread_${generateId()}`;
      const postObject: TResponseApiRequestBody = {
        input: `${message} <security> ${MEETING_META_INSTRUCTION} ${final_result}</security>`,
        model: llm_selection?.model ?? "",
        metadata: {
          assistant_ref: llm_selection?.assistant_ref ?? null,
          thread_ref: thread,
        },
      };
      const systemResponse = await postMessageToResponse(postObject);
      const systemMessage = systemResponse?.output.at(-1) as OutputMessageType;

      if (!thread_ref || thread_ref === "") {
        const newValue = getUpdatedMeetingValue(meetingValue, {
          thread_ref: thread,
        });
        dispatch(setMeetingValue(newValue));

        dispatch(
          editStorageObject({
            id: selectedId,
            name: selectedObject?.friendly_name ?? "",
            value: newValue,
            service: "meetings",
          }),
        );
      }

      return systemMessage;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const startMeetingChatAction = createAsyncThunk(
  "meeting/startMeetingChat",
  async (message: string, { rejectWithValue, getState }) => {
    try {
      const { meetingValue } = (getState() as RootState).meetingState;
      if (!meetingValue || !meetingValue.meetings_object) return;
      const { thread_ref, final_result } = meetingValue.meetings_object;
      const postObject: TChatCompletionRequest = {
        model: "gpt-4o",
        messages: [
          {
            role: "user",
            content: `${message} <security>${final_result}</security>`,
          },
        ],
        metadata: {
          assistant_ref: null,
          thread_ref: thread_ref,
        },
      };
      const systemResponse = await postMessageToCompletion(postObject);
      const systemMessage = systemResponse?.choices.at(-1)?.message.content;

      return systemMessage;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getMeetingMessagesAction = createAsyncThunk(
  "meeting/getMeetingMessages",
  async (threadRef: string, { getState, rejectWithValue }) => {
    try {
      const { meetingValue, messages } = (getState() as RootState).meetingState;
      if (!meetingValue?.meetings_object) return;
      const responseMessages = await getMessagesFromThread(threadRef);
      const receivedMessages = transformMessagesObject(responseMessages);
      const newMessages = mergedDialogMessages(messages, receivedMessages);

      const updatedMessages = newMessages.map((msg) => {
        if (msg.role === "user") {
          const updatedContent = hiddenSecurityPart(msg.content as string);
          return { ...msg, content: updatedContent };
        }
        return msg;
      });

      return updatedMessages;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteMeetingThreadAction = createAsyncThunk(
  "meeting/deleteMeetingThread",
  async (thread_ref: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const { meetingValue } = (getState() as RootState).meetingState;
      const { selectedId, data } = (getState() as RootState).storageObjectState
        .meetings;
      if (!selectedId || !meetingValue?.meetings_object) return;
      const selectedObject = data.entities[selectedId];
      if (!selectedObject) return;
      dispatch(clearMessages());
      dispatch(
        updateMeetingValueField({
          thread_ref: null,
        }),
      );

      const updatedValue: MeetingsValue = {
        meetings_object: {
          ...meetingValue?.meetings_object,
          thread_ref: null,
        },
      };

      dispatch(
        editStorageObject({
          id: selectedId,
          name: selectedObject?.friendly_name,
          value: updatedValue,
          service: "meetings",
        }),
      );
      await deleteThread(thread_ref);
      return;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
